import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators'
import { Store } from '@ngrx/store';
import { includes, pluck } from 'ramda';
import { Observable, of, EMPTY } from 'rxjs';
import { catchError, filter, map, switchMap, tap, mergeMap, take } from 'rxjs/operators';

import * as meet from '../actions/meet';
import * as customer from '../actions/customer';
import * as user from '../actions/user';
import * as care from '../actions/care';
import { MeetService } from '../meet/meet.service';
import * as fromRoot from '../reducers';
import { AppConstants } from '../app.constants';
import { EngageConstants } from '../engage/engage.constants';
import { MeetConstants } from '../meet/meet.constants';
import { SharedConstants } from '../shared/shared.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

@Injectable()
export class MeetEffects {
  public loadFilters$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(
      meet.ActionTypes.LOAD_FILTERS,
      user.ActionTypes.SELECT_LOCATION,
      customer.ActionTypes.CUSTOMERS_SELECTED_FOR_ENGAGE,
      care.ActionTypes.LOAD_CARES_PAGE
    ),
    map((action: any) => action.payload),
    concatLatestFrom(() => this.store.select(fromRoot.areFiltersLoaded)),
    filter(([action, areFiltersLoaded]) => !areFiltersLoaded),
    concatLatestFrom(() => this.store.select(fromRoot.getFeatureEnabled(SharedConstants.betaFilters)).pipe(take(1))),
    switchMap(([action, betaFilters]) => this.ms.loadFilters().pipe(
      map((res) => new meet.LoadFiltersSuccess({ filters: res, betaFilters })),
      catchError((error) => of(new meet.LoadFiltersFailure(error)))
    ))
  )})

  public loadSavedGroups$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.TOGGLE_CATEGORY_EXPANSION),
    map((action: any) => action.payload),
    concatLatestFrom(() => this.store.select(fromRoot.getAllSavedGroups)),
    filter(([payload, savedGroups]) => {
      return payload.category.id === MeetConstants.savedGroupsFilterId && !payload.category.open && savedGroups.length === 0;
    }),
    switchMap(() => this.ms.loadSavedGroups().pipe(
      map((res) => new meet.LoadSavedGroupsSuccess(res)),
      catchError((error) => of(new meet.LoadSavedGroupsFailure(error)))
    ))
  )})

  public loadSavedGroupsForNewLocation$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(user.ActionTypes.GET_LOCATION_DETAILS_SUCCESS),
    map((action: any) => action.payload),
    concatLatestFrom(() => this.store.select(fromRoot.isSavedGroupsCategoryOpen)),
    filter(([payload, isSavedGroupsOpen]) => !!isSavedGroupsOpen),
    switchMap(() => this.ms.loadSavedGroups().pipe(
      map((res) => new meet.LoadSavedGroupsSuccess(res)),
      catchError((error) => of(new meet.LoadSavedGroupsFailure(error)))
    ))
  )})

  public getSelectedSavedGroupCustomers$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.LOAD_SELECTED_SAVED_GROUP_CUSTOMERS),
    map((action: any) => action.payload),
    switchMap((groupId) => this.ms.loadSelectedSavedGroupCustomers(groupId).pipe(
      map((res) => new meet.LoadSelectedSavedGroupCustomersSuccess(res)),
      catchError((error) => of(new meet.LoadSelectedSavedGroupCustomersFailure(error)))
    ))
  )})

  public updateSavedGroup$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.UPDATE_SAVED_GROUP),
    map((action: any) => action.payload),
    concatLatestFrom(() => this.store.select(fromRoot.getUpdateSavedGroupPayload).pipe(take(1))),
    switchMap(([name, group]) => this.ms.updateSavedGroup({ ...group, name }).pipe(
      map(() => new meet.UpdateSavedGroupSuccess({ ...group, name })),
      catchError((error) => of(new meet.UpdateSavedGroupError(error)))
    ))
  )});

  public deleteSavedGroupFilter$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.DELETE_SAVED_GROUP),
    map((action: any) => action.payload),
    switchMap((group) => this.ms.deleteSavedGroup(group.id).pipe(
      map((res) => new meet.DeleteSavedGroupSuccess(group)),
      catchError((error) => of(new meet.DeleteSavedGroupError(error)))
    ))
  )});

  public refreshSavedGroupsOnDelete$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.DELETE_SAVED_GROUP_SUCCESS),
    map((action: any) => action.payload),
    concatLatestFrom(() => this.store.select(fromRoot.getMeetSelectedSavedGroups)),
    switchMap(([group, selectedSavedGroups]) => this.ms.loadSavedGroups().pipe(
      mergeMap((res) => {
        let actionArray: any[] = [new meet.LoadSavedGroupsSuccess(res)];
        let selectedGroupIds = pluck('id', selectedSavedGroups);

        // Only dispatch RemoveSavedGroup action if the saved filter is currently applied
        if (includes(group.id, selectedGroupIds)) {
          actionArray.push(new meet.RemoveSavedGroup({ savedGroup : group, page: AppConstants.meetPage }));
        }
        return actionArray;
      }),
      catchError((error) => of(new meet.LoadSavedGroupsFailure(error)))
    ))
  )});

  public refreshSavedGroupsOnCreate$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(
      meet.ActionTypes.CREATE_SAVED_GROUP_SUCCESS,
      meet.ActionTypes.UPDATE_SAVED_GROUP_SUCCESS
    ),
    map((action: any) => action.payload),
    switchMap(() => this.ms.loadSavedGroups().pipe(
      map((res) => new meet.LoadSavedGroupsSuccess(res)),
      catchError((error) => of(new meet.LoadSavedGroupsFailure(error)))
    ))
  )});

  public createSavedGroup: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.CREATE_SAVED_GROUP),
    map((action: any) => action.payload),
    concatLatestFrom(() => this.store.select(fromRoot.getCreateGroupRequest)),
    switchMap(([groupName, groupPayload]) => this.ms.createSavedGroup({ ...groupPayload, name: groupName }).pipe(
      map((group) => new meet.CreateSavedGroupSuccess(group)),
      catchError((error) => of(new meet.CreateSavedGroupError(error)))
    ))
  )});

  public createSavedGroupSuccess$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.CREATE_SAVED_GROUP_SUCCESS),
    tap(() => {
      this.snackBar.open(
        `Saved Group has been successfully created.`,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch : false });

  public createSavedGroupError$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.CREATE_SAVED_GROUP_ERROR),
    tap((error) => {
      this.snackBar.open(
        error,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch: false });

  public deleteSavedGroupSuccess$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.DELETE_SAVED_GROUP_SUCCESS),
    tap(() => {
      this.snackBar.open(
        `Saved Group has been successfully deleted.`,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch : false });

  public updateSavedGroupError$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.UPDATE_SAVED_GROUP_ERROR),
    tap(() => {
      this.snackBar.open(
        `Failed to update saved group, please try again later.`,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch:false });

  public deleteSavedGroupError$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.DELETE_SAVED_GROUP_ERROR),
    tap(() => {
      this.snackBar.open(
        `Failed to delete saved group, please try again later.`,
        'Dismiss', {
          duration: 5000
        }
      );
    })
  )}, { dispatch : false });

  // When the user removes the final selected filter on mobile navigate them back to the filter selection page
  public goToFilterSelection$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(
      meet.ActionTypes.REMOVE_SELECTED_FILTER,
      meet.ActionTypes.REMOVE_INCLUDE_CUSTOMERS,
      meet.ActionTypes.REMOVE_INDIVIDUAL_EXCLUDE_CUSTOMER,
      meet.ActionTypes.CLEAR_FILTERS,
      meet.ActionTypes.REMOVE_SAVED_GROUP
    ),
    map((action: any) => action.payload),
    concatLatestFrom((payload) => {
      const page = payload.page;
      return this.store.select(fromRoot.isMobileWithFiltersCount(page));
    }),
    concatLatestFrom((payload) => {
      return this.store.select(fromRoot.getFeatureEnabled(SharedConstants.campaignEngineV2));
    }),
    filter(([[filter, removedAllFilters], campaignEngineV2]) => removedAllFilters),
    map(([[filter, removedAllFilters], campaignEngineV2]) => {
      const page = filter.page;
      let previousRoute;

      // If we have campaign context (type and mode), navigate to campaign-details
      if (filter.type && filter.mode) {
        previousRoute = '/engage/campaign-details';
      } else if ( page == AppConstants.meetPage ) {
        previousRoute = '/meet';
      } else if ( page == AppConstants.engagePage ) {
        previousRoute = '/engage/engage-details';
      } else if ( page == SharedConstants.campaignPage ) {
        previousRoute = 'engage/campaign-details'
      }

      const queryParams: any = {
        previousRoute,
        page,
        ...(filter.type && { type: filter.type, mode: filter.mode }),
        ...(filter.type && filter.id && { id: filter.id })
      };
      this.router.navigate(['/filter/filter-display-for-mobile'], { queryParams });
    }),
  )}, { dispatch : false });

  // When LD Flag is ON, sets "Primarily Visits My Location" to true by default
  // Does not set default if customers are already selected or default audience is already applied
  public preselectPrimarilyVisitsMyLocations$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.LOAD_FILTERS),
    filter((action: any) => action.payload.page === AppConstants.engagePage),
    concatLatestFrom(() => [
      this.store.select(fromRoot.getFeatureEnabled(EngageConstants.preselectPrimarilyVisitsMyLocation)).pipe(take(1)),
      this.store.select(fromRoot.getEngageDefaultAudienceApplied).pipe(take(1)),
      this.store.select(fromRoot.getEngageAllSelectedCustomerIds).pipe(take(1))
    ]),
    switchMap(([_, primarilyVisitsMyLocationFlagEnabled, engageDefaultAudienceApplied, selectedCustomerIds]) => {
      const hasSelectedCustomers = selectedCustomerIds && selectedCustomerIds.length > 0;
      if (primarilyVisitsMyLocationFlagEnabled && !hasSelectedCustomers) {
        const actionsToDispatch: any[] = [
          new meet.ToggleCategoryExpansion({ category: { id: 'customer-actions' }, page: AppConstants.engagePage })
        ];
        if (!engageDefaultAudienceApplied) {
          actionsToDispatch.push(
            new meet.AddSelectedFilter({
              filter: {
                id: 'primarily-visit-my-restaurant',
                parentCategory: 'customer-actions',
                name: 'Primarily Visit My Restaurant',
                selectedButtonToggle: 'does'
              },
              page: AppConstants.engagePage
            })
          );
        }
        return of(...actionsToDispatch);
      }
      return EMPTY;
    })
  )});

  // Remove "Primarily Visits My Location" filter when an individual customer is added on the engage page
  public removePrimarilyVisitsOnIndividualCustomerAdd$: Observable<any> = createEffect(() => { return this.actions$.pipe(
    ofType(meet.ActionTypes.ADD_INDIVIDUAL_INCLUDE_CUSTOMER),
    map((action: any) => action.payload),
    filter((payload) => payload.page === AppConstants.engagePage),
    concatLatestFrom(() => this.store.select(fromRoot.getEngageSelectedFilters).pipe(take(1))),
    switchMap(([payload, selectedFilters]) => {
      const primarilyVisitsFilter = selectedFilters.find(f => f.id === 'primarily-visit-my-restaurant');
      if (primarilyVisitsFilter) {
        return of(new meet.RemoveSelectedFilter({
          filter: primarilyVisitsFilter,
          page: AppConstants.engagePage
        }));
      }
      return EMPTY;
    })
  )});

  constructor(
    private actions$: Actions,
    private ms: MeetService,
    private store: Store<fromRoot.State>,
    private snackBar: MatSnackBar,
    private router: Router
  ) { }
}
